import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {ThemeProvider as StyledThemeProvider} from 'styled-components';
import restaurantConfig from '../config/restaurantConfig';
import {useMerchant} from './MerchantContext';
import {MerchantThemeDto} from '../types';

// Default theme in case API fails or is loading
const defaultTheme: MerchantThemeDto = {
  primary: '#e74c3c',
  secondary: '#c0392b',
  accent: '#2ecc71',
  background: '#ffffff',
  text: '#2d3436',
  lightText: '#636e72',
  success: '#4CAF50',
  error: '#F44336',
};

// Define the theme context type
type ThemeContextType = {
  colors: MerchantThemeDto;
  images: typeof restaurantConfig.images;
};

// Create the theme context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Define props for ThemeProvider
interface ThemeProviderProps {
  children: ReactNode;
}

// Create the ThemeProvider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { restaurant } = useMerchant();
  const [colors, setColors] = useState<MerchantThemeDto>(defaultTheme);

  useEffect(() => {
    if (restaurant && restaurant.theme) {
      setColors(restaurant.theme);
    }
  }, [restaurant]);

  // Get theme colors and images from the restaurant configuration
  const themeValue = {
    colors,
    images: restaurantConfig.images,
  };

  return (
    <ThemeContext.Provider value={themeValue}>
      <StyledThemeProvider theme={colors}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

// Create a custom hook to use the theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Export the theme directly for components that don't use the context
export const theme = {
  colors: defaultTheme,
  images: restaurantConfig.images,
};